import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { customToast } from "../../../components/notify/NotifyContainer";
import { useDateContext } from "../../analyze/context";
import { useIsProjectOwner } from "../../projects/hooks";
import { useDownloadChatHistoryByDateRangeMutation } from "../services/charactersApiSlice";
import { validateChatHistoryDateRange } from "../utils";

dayjs.extend(utc);

const useChatHistoryDownload = () => {
    const { t } = useTranslation();
    const [fetchDownloadByDateRange, isFetchingByDateRange] = useDownloadChatHistoryByDateRangeMutation();
    const { characterId, projectId } = useParams();
    const isProjectOwner = useIsProjectOwner({ projectId });
    const { dateRange } = useDateContext();

    const handleDownload = async () => {
        try {
            if (!characterId || !projectId) {
                return;
            }

            const { started_at, ended_at } = dateRange;

            if (!started_at || !ended_at) {
                customToast.error(t("CH.Chat_History_Error_Select_Date") || "請選擇日期範圍");
                return;
            }

            const validationResult = validateChatHistoryDateRange(started_at, ended_at, t);
            if (!validationResult.isValid) {
                customToast.error(validationResult.errorMessage || "日期範圍驗證失敗");
                return;
            }

            // 格式化日期為 UTC 時區 (ISO 8601 格式)
            // 前端永遠傳給後端 UTC+0 時間，UI 顯示仍使用用戶本地時區
            const startTime = started_at.startOf("hour").utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
            const endTime = ended_at.startOf("hour").utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

            const res = await fetchDownloadByDateRange({
                character_id: characterId,
                start_time: startTime,
                end_time: endTime,
            }).unwrap();

            if (res?.status === 1 && res?.data?.file_url) {
                window.open(res?.data?.file_url, "_blank");
            } else {
                customToast.error(res?.message || "Something wrong. Please try again later.");
            }
        } catch (err) {
            customToast.error("Something wrong. Please try again later.");
        }
    };

    const handleDownloadCurrentMonth = async () => {
        try {
            if (!characterId || !projectId) {
                return;
            }

            const now = dayjs();
            const firstDayOfMonth = now.startOf("month").startOf("day");
            // 格式化為 UTC 時區
            const startTime = firstDayOfMonth.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
            const endTime = now.startOf("hour").utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

            const res = await fetchDownloadByDateRange({
                character_id: characterId,
                start_time: startTime,
                end_time: endTime,
            }).unwrap();

            if (res?.status === 1 && res?.data?.file_url) {
                window.open(res?.data?.file_url, "_blank");
            } else {
                customToast.error(res?.message || "Something wrong. Please try again later.");
            }
        } catch (err) {
            customToast.error("Something wrong. Please try again later.");
        }
    };

    return {
        handleDownload,
        handleDownloadCurrentMonth,
        isProjectOwner,
        isFetching: {
            isLoading: isFetchingByDateRange.isLoading,
        },
    };
};
export default useChatHistoryDownload;
